import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import type { Tables } from '@limo/database'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useToast } from '@/hooks/use-toast'
import {
  Plus,
  FileText,
  MoreHorizontal,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  ExternalLink,
  Navigation,
} from 'lucide-react'
import { CreateCustomPageDialog } from '@/components/custom-pages/CreateCustomPageDialog'

type CustomPage = Tables<'custom_pages'>

export function CustomPagesPage() {
  const { account } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { toast } = useToast()

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string
    title: string
  } | null>(null)

  // Use impersonated account when admin is impersonating
  const activeAccount = isImpersonating ? impersonatedAccount : account

  // Get the base path for navigation
  const basePath = isImpersonating
    ? `/admin/accounts/${impersonatedAccount?.slug}/view`
    : '/dashboard'

  // Fetch custom pages
  const { data: pages, isLoading } = useQuery({
    queryKey: ['custom-pages', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return []
      const { data, error } = await supabase
        .from('custom_pages')
        .select('*')
        .eq('account_id', activeAccount.id)
        .order('navigation_order')
      if (error) throw error
      return data as CustomPage[]
    },
    enabled: !!activeAccount?.id,
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('custom_pages').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-pages'] })
      toast({
        title: 'Page deleted',
        description: 'The page has been successfully deleted.',
      })
      setDeleteTarget(null)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  // Toggle publish status
  const togglePublishMutation = useMutation({
    mutationFn: async ({
      id,
      is_published,
    }: {
      id: string
      is_published: boolean
    }) => {
      const { error } = await supabase
        .from('custom_pages')
        .update({ is_published })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: (_, { is_published }) => {
      queryClient.invalidateQueries({ queryKey: ['custom-pages'] })
      toast({
        title: is_published ? 'Page published' : 'Page unpublished',
        description: is_published
          ? 'The page is now visible on your website.'
          : 'The page is no longer visible on your website.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const handleEdit = (pageId: string) => {
    navigate(`${basePath}/pages/${pageId}/edit`)
  }

  const getPreviewUrl = (slug: string) => {
    // Build the public viewer URL
    const viewerBase =
      import.meta.env.VITE_VIEWER_URL || 'http://localhost:3001'
    return `${viewerBase.replace('localhost', `${activeAccount?.slug}.localhost`)}/p/${slug}`
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Custom Pages</h1>
          <p className="text-muted-foreground">
            Create and manage custom pages for your website
          </p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Create Page
        </Button>
      </div>

      {/* Pages Table */}
      {pages && pages.length > 0 ? (
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Navigation</TableHead>
                <TableHead className="w-[70px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pages.map((page) => (
                <TableRow key={page.id}>
                  <TableCell className="font-medium">{page.title}</TableCell>
                  <TableCell className="text-muted-foreground">
                    /p/{page.slug}
                  </TableCell>
                  <TableCell>
                    {page.is_published ? (
                      <Badge variant="default" className="bg-green-600">
                        Published
                      </Badge>
                    ) : (
                      <Badge variant="secondary">Draft</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {page.show_in_navigation ? (
                      <Badge variant="outline" className="gap-1">
                        <Navigation className="h-3 w-3" />
                        In Nav
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                          <span className="sr-only">Open menu</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEdit(page.id)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Edit
                        </DropdownMenuItem>
                        {page.is_published && (
                          <DropdownMenuItem asChild>
                            <a
                              href={getPreviewUrl(page.slug)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <ExternalLink className="mr-2 h-4 w-4" />
                              View Page
                            </a>
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={() =>
                            togglePublishMutation.mutate({
                              id: page.id,
                              is_published: !page.is_published,
                            })
                          }
                        >
                          {page.is_published ? (
                            <>
                              <EyeOff className="mr-2 h-4 w-4" />
                              Unpublish
                            </>
                          ) : (
                            <>
                              <Eye className="mr-2 h-4 w-4" />
                              Publish
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={() =>
                            setDeleteTarget({ id: page.id, title: page.title })
                          }
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        // Empty state
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-12">
          <FileText className="mb-4 h-12 w-12 text-muted-foreground/50" />
          <h3 className="mb-2 text-lg font-semibold">No custom pages yet</h3>
          <p className="mb-4 text-center text-muted-foreground">
            Create custom pages like Privacy Policy, Terms of Service, or About
            Us to add to your website.
          </p>
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create Your First Page
          </Button>
        </div>
      )}

      {/* Create Dialog */}
      <CreateCustomPageDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        accountId={activeAccount?.id || ''}
        onSuccess={(pageId) => {
          setCreateDialogOpen(false)
          handleEdit(pageId)
        }}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={() => setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Page</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteTarget?.title}"? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
